import { Injectable } from '@angular/core';
import { Run } from '../../runs/run.model';
import { RunSource } from '../enums/run-source.enum';
import { ParsedRunInfo, parseRunName, baseConfigsMatch } from '../utils/run-parser.util';

export interface PnLDeltaInfo {
  delta: number;  // In millions
  matchedBaseRunName: string;
  tcDays: number | null;
  breakevenTcRate: number | null;  // In $k/day
}

@Injectable({
  providedIn: 'root'
})
export class PnLDeltaService {
  
  /**
   * Pre-compute P&L deltas for all Freight runs in O(n) time.
   * Returns a Map from run.runId to PnLDeltaInfo.
   */
  computeDeltas(runs: Run[]): Map<string, PnLDeltaInfo> {
    const deltaMap = new Map<string, PnLDeltaInfo>();
    
    // Step 1: Parse all runs once and categorize them
    const parsedRuns: { run: Run; info: ParsedRunInfo; latestTime: Date | null }[] = [];
    
    for (const run of runs) {
      const info = parseRunName(run.name);
      const latestTime = this.getLatestEventTime(run);
      parsedRuns.push({ run, info, latestTime });
    }
    
    // Step 2: Build a list of Base runs with their parsed info
    const baseRuns = parsedRuns.filter(p => 
      p.info.source === RunSource.FreightBase &&
      p.run.optimisationResult?.status === 'Optimal' &&
      p.run.optimisationResult?.pnL?.value !== undefined &&
      p.latestTime !== null
    );
    
    // Step 3: For each Freight run, find matching Base run
    const windowMs = 6 * 60 * 60 * 1000; // 6 hours
    
    for (const { run, info, latestTime } of parsedRuns) {
      // Only process Freight runs
      if (info.source !== RunSource.Freight) continue;
      if (!info.baseConfiguration) continue;
      if (!latestTime) continue;
      if (run.optimisationResult?.status !== 'Optimal') continue;
      
      const freightPnL = run.optimisationResult?.pnL?.value;
      if (freightPnL === undefined || freightPnL === null) continue;
      
      // Find matching Base runs within time window
      const candidates: { baseRun: Run; baseName: string; timeDiff: number; baseTime: Date }[] = [];
      
      for (const base of baseRuns) {
        // Must have matching base configuration
        if (!baseConfigsMatch(info.baseConfiguration, base.info.baseConfiguration)) continue;
        
        const timeDiff = Math.abs(latestTime.getTime() - base.latestTime!.getTime());
        if (timeDiff > windowMs) continue;
        
        candidates.push({
          baseRun: base.run,
          baseName: base.run.name,
          timeDiff,
          baseTime: base.latestTime!
        });
      }
      
      if (candidates.length === 0) continue;
      
      // Sort by time difference (closest first)
      candidates.sort((a, b) => {
        if (a.timeDiff !== b.timeDiff) return a.timeDiff - b.timeDiff;
        return b.baseTime.getTime() - a.baseTime.getTime();
      });
      
      const bestMatch = candidates[0];
      const basePnL = bestMatch.baseRun.optimisationResult?.pnL?.value;
      if (basePnL === undefined || basePnL === null) continue;
      
      const delta = (freightPnL - basePnL) / 1_000_000;
      const tcDays = info.tcDays;
      const breakevenTcRate = tcDays && tcDays > 0 ? (-delta * 1000) / tcDays : null;
      
      deltaMap.set(run.runId, {
        delta,
        matchedBaseRunName: bestMatch.baseName,
        tcDays,
        breakevenTcRate
      });
    }
    
    return deltaMap;
  }
  
  private getLatestEventTime(run: Run): Date | null {
    if (!run.runHistories || run.runHistories.length === 0) return null;
    const latest = run.runHistories.reduce((a, b) => 
      new Date(a.eventTime) > new Date(b.eventTime) ? a : b
    );
    return new Date(latest.eventTime);
  }
}
